import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Skeleton } from '@ordo/ui/skeleton'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useAppConfig, useDeleteAppConfig, useUpdateAppConfig } from 'hooks/api'
import { RotateCcw, Save, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

interface ProviderField {
  key: string
  label: string
  type?: 'password' | 'text'
}

const PROVIDERS: Array<{
  name: string
  description: string
  fields: ProviderField[]
}> = [
  {
    name: 'SendGrid',
    description:
      'Email delivery credentials and the org-default physical address attached to every verified sender (CAN-SPAM requirement). Manage individual from-addresses on the Senders page.',
    fields: [
      { key: 'SENDGRID_API_KEY', label: 'API Key', type: 'password' },
      { key: 'SENDGRID_SENDER_ADDRESS', label: 'Address Line 1' },
      { key: 'SENDGRID_SENDER_ADDRESS_2', label: 'Address Line 2' },
      { key: 'SENDGRID_SENDER_CITY', label: 'City' },
      { key: 'SENDGRID_SENDER_STATE', label: 'State / Region' },
      { key: 'SENDGRID_SENDER_ZIP', label: 'Postal Code' },
      { key: 'SENDGRID_SENDER_COUNTRY', label: 'Country' },
    ],
  },
  {
    name: 'Twilio',
    description: 'SMS messaging service configuration.',
    fields: [
      { key: 'TWILIO_ACCOUNT_SID', label: 'Account SID' },
      { key: 'TWILIO_AUTH_TOKEN', label: 'Auth Token', type: 'password' },
      { key: 'TWILIO_FROM_NUMBER', label: 'From Number' },
    ],
  },
  {
    name: 'Front',
    description: 'Front inbox integration configuration.',
    fields: [{ key: 'FRONT_API_TOKEN', label: 'API Token', type: 'password' }],
  },
  {
    name: 'Customer.io',
    description: 'Source credentials for one-way template/layout/campaign imports.',
    fields: [{ key: 'CUSTOMERIO_API_KEY', label: 'API Key', type: 'password' }],
  },
]

export function AdminSection() {
  const { data: config, loading, refetch } = useAppConfig()
  const { perform: updateConfig, loading: saving } = useUpdateAppConfig()
  const { perform: deleteConfig } = useDeleteAppConfig()

  // Track edited values per key. Undefined means no edit; empty string means user cleared.
  const [edits, setEdits] = useState<Record<string, string>>({})

  // Reset edits when config data refreshes
  useEffect(() => {
    setEdits({})
  }, [config])

  const handleFieldChange = useCallback((key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleSaveProvider = useCallback(
    async (fields: ProviderField[]) => {
      const configs: Array<{ key: string; value: string }> = []
      for (const field of fields) {
        const edited = edits[field.key]
        if (edited !== undefined && edited.length > 0) {
          configs.push({ key: field.key, value: edited })
        }
      }
      if (configs.length === 0) {
        return
      }
      await updateConfig({ configs })
      refetch()
    },
    [edits, updateConfig, refetch],
  )

  const handleClear = useCallback(
    async (key: string) => {
      await deleteConfig(key)
      refetch()
    },
    [deleteConfig, refetch],
  )

  const hasProviderEdits = (fields: ProviderField[]) => {
    return fields.some((f) => {
      const edited = edits[f.key]
      return edited !== undefined && edited.length > 0
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="text-sm">
          <p className="font-medium text-amber-500">Shared Settings</p>
          <p className="mt-1 text-muted-foreground">
            These integration settings are shared across all users. Changes will take effect immediately for the entire
            application.
          </p>
        </div>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <div className="space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        }>
        {PROVIDERS.map((provider) => (
          <Card key={provider.name}>
            <CardHeader>
              <CardTitle>{provider.name}</CardTitle>
              <CardDescription>{provider.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {provider.fields.map((field) => {
                  const entry = config?.[field.key]
                  const editedValue = edits[field.key]
                  const isEdited = editedValue !== undefined

                  return (
                    <div key={field.key} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <label htmlFor={field.key} className="text-sm font-medium text-muted-foreground">
                          {field.label}
                        </label>
                        {entry?.isSet && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                            {entry.source === 'env' ? 'ENV' : 'DB'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          id={field.key}
                          type={field.type ?? 'text'}
                          placeholder={entry?.isSet ? (entry.masked ?? '') : 'Not configured'}
                          value={isEdited ? editedValue : ''}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          className="min-w-0 flex-1"
                        />
                        {entry?.source === 'db' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            title="Clear (revert to environment default)"
                            onClick={() => handleClear(field.key)}>
                            <RotateCcw className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
                <div className="flex justify-end pt-2">
                  <Button
                    size="sm"
                    disabled={saving || !hasProviderEdits(provider.fields)}
                    onClick={() => handleSaveProvider(provider.fields)}>
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </LoadingWrapper>
    </div>
  )
}
